/**
 * Whop checkout bridge — Shopify theme intercept.
 *
 * Paste into your theme as a snippet (or an asset loaded from theme.liquid):
 *
 *   <script>window.WHOP_BRIDGE_URL = "https://YOUR-CHECKOUT-DOMAIN";</script>
 *   <script src="{{ 'whop-intercept.js' | asset_url }}" defer></script>
 *
 * Cart checkout reads the current cart before applying pending form quantities.
 * A write requires the current cart to match the form's rendered quantities.
 * Quantity controls must expose data-line-key="{{ item.key }}" (or data-key),
 * or use name="updates[{{ item.key }}]". A variant ID/index is not a line key.
 * Links without a cart form read /cart.js. A product-page Buy now button uses
 * its own selected variant and quantity. Both use a top-level form POST;
 * normal cart browsing and Add to cart remain untouched.
 */
(function () {
  var BRIDGE = window.WHOP_BRIDGE_URL;
  if (!BRIDGE) {
    console.warn("[whop-bridge] window.WHOP_BRIDGE_URL is not set");
    return;
  }

  var redirecting = false;
  var navigationVersion = 0;
  var cartRequest = null;
  var cartTimer = 0;
  var handoffForm = null;
  var cartNotice = null;

  function showCartError(form) {
    if (cartNotice) cartNotice.remove();
    cartNotice = document.createElement("p");
    cartNotice.role = "alert";
    cartNotice.textContent = "Please refresh your cart to confirm its items and quantities before checkout. If this continues, contact the store.";
    (form && form.appendChild ? form : document.body).appendChild(cartNotice);
  }

  function cartEndpoint(form, update) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== "string" || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root)) {
      var locale = /\/([a-z]{2}(?:-[a-z]{2})?)\/cart(?:\.js)?\/?(?:[?#]|$)/i.exec(form && form.action || "");
      root = locale ? "/" + locale[1] + "/" : "/";
    }
    return root + (update ? "cart/update.js" : "cart.js");
  }

  function resetNavigation() {
    navigationVersion += 1;
    redirecting = false;
    clearTimeout(cartTimer);
    if (cartRequest) cartRequest.abort();
    cartRequest = null;
    if (handoffForm) handoffForm.remove();
    handoffForm = null;
  }

  // Safari/Chrome can restore the same document, including its click lock.
  window.addEventListener("pagehide", resetNavigation);
  window.addEventListener("pageshow", resetNavigation);

  function submitPayload(payload) {
    var form = document.createElement("form");
    form.method = "POST";
    form.action = BRIDGE.replace(/\/$/, "") + "/api/handoff";
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = "payload";
    input.value = JSON.stringify(payload);
    form.appendChild(input);
    document.body.appendChild(form);
    handoffForm = form;
    try {
      form.submit();
    } catch (error) {
      resetNavigation();
      console.error("[whop-bridge] checkout navigation failed", error);
    }
  }

  function productPayload(button) {
    var form = button.form || (button.closest && button.closest("form"));
    if (!form || !/\/cart\/add(?:[.?/]|$)/.test(form.action || "")) return null;
    var data = new FormData(form);
    var variantId = Number(data.get("id"));
    var quantity = Number(data.get("quantity") || "1");
    if (!Number.isSafeInteger(variantId) || variantId <= 0 ||
        !Number.isSafeInteger(quantity) || quantity <= 0 || quantity > 200) return null;
    return { items: [{ id: variantId, quantity: quantity }] };
  }

  function cartUpdates(form) {
    if (!form || !/\/cart(?:\.js)?\/?(?:[?#]|$)/.test(form.action || "")) return null;
    var indexed = [];
    var keyed = Object.create(null);
    var data = new FormData(form);
    for (var entry of data.entries()) {
      var name = entry[0];
      var key = /^updates\[([^\]]+)\]$/.exec(name);
      if (name !== "updates[]" && !key) continue;
      var raw = entry[1];
      if (typeof raw !== "string" || !/^\d+$/.test(raw.trim())) throw new Error("Invalid cart quantity");
      var quantity = Number(raw);
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 200) throw new Error("Invalid cart quantity");
      if (name === "updates[]") indexed.push(quantity);
      else {
        // A variant id can refer to multiple lines or add a removed item back.
        // Only Shopify's full line key identifies the rendered line here.
        if (!/^[1-9]\d*:[A-Za-z0-9_-]{1,128}$/.test(key[1]) ||
            Object.prototype.hasOwnProperty.call(keyed, key[1])) throw new Error("Ambiguous cart line");
        keyed[key[1]] = quantity;
      }
    }
    if (indexed.length && Object.keys(keyed).length) throw new Error("Ambiguous cart quantities");
    if (indexed.length) {
      var controls = Array.prototype.filter.call(form.elements || [], function (control) {
        return control.name === "updates[]" && !control.disabled;
      });
      if (controls.length !== indexed.length) throw new Error("Cart line identity unavailable");
      controls.forEach(function (control, index) {
        var lineKey = control.getAttribute("data-line-key") || control.getAttribute("data-key");
        var alternateKey = control.getAttribute("data-key");
        var variantId = control.getAttribute("data-quantity-variant-id");
        if (typeof lineKey !== "string" || !/^[1-9]\d*:[A-Za-z0-9_-]{1,128}$/.test(lineKey) ||
            (alternateKey && alternateKey !== lineKey) || (variantId && lineKey.split(":")[0] !== variantId) ||
            Number(control.value) !== indexed[index] || Object.prototype.hasOwnProperty.call(keyed, lineKey)) {
          throw new Error("Ambiguous cart line");
        }
        keyed[lineKey] = indexed[index];
      });
    }
    return Object.keys(keyed).length ? keyed : null;
  }

  function cartMatchesUpdates(cart, updates) {
    if (!updates) return true;
    if (!Array.isArray(cart.items)) return false;
    var seen = Object.create(null);
    for (var item of cart.items) {
      if (!Object.prototype.hasOwnProperty.call(updates, item.key) || seen[item.key] ||
          item.quantity <= 0 || item.quantity !== updates[item.key] || String(item.variant_id) !== item.key.split(":")[0]) return false;
      seen[item.key] = true;
    }
    return Object.keys(updates).every(function (key) { return updates[key] === 0 || seen[key]; });
  }

  function cartBaseline(form, updates) {
    if (!updates) return null;
    var baseline = Object.create(null);
    var controls = Array.prototype.filter.call(form && form.elements || [], function (control) {
      return !control.disabled && /^updates\[[^\]]*\]$/.test(control.name || "");
    });
    for (var control of controls) {
      var named = /^updates\[([^\]]+)\]$/.exec(control.name);
      var attribute = control.getAttribute("data-line-key");
      var alternate = control.getAttribute("data-key");
      var key = named ? named[1] : attribute || alternate;
      var raw = control.defaultValue;
      if (!Object.prototype.hasOwnProperty.call(updates, key) || baseline[key] !== undefined ||
          (attribute && attribute !== key) || (alternate && alternate !== key) ||
          Number(control.value) !== updates[key] || typeof raw !== "string" || !/^\d+$/.test(raw.trim())) return null;
      var quantity = Number(raw);
      if (!Number.isSafeInteger(quantity) || quantity <= 0 || quantity > 200) return null;
      baseline[key] = quantity;
    }
    return Object.keys(baseline).length === Object.keys(updates).length ? baseline : null;
  }

  function sameQuantities(left, right) {
    if (!left || !right) return left === right;
    return Object.keys(left).length === Object.keys(right).length && Object.keys(left).every(function (key) {
      return Object.prototype.hasOwnProperty.call(right, key) && left[key] === right[key];
    });
  }

  function goToBridge(form) {
    if (redirecting) return;
    var updates;
    var baseline;
    try {
      updates = cartUpdates(form);
      baseline = cartBaseline(form, updates);
    } catch (error) {
      console.error("[whop-bridge] invalid cart quantities", error);
      showCartError(form);
      return;
    }
    if (cartNotice) { cartNotice.remove(); cartNotice = null; }
    redirecting = true;
    var version = navigationVersion;
    var controller = new AbortController();
    cartRequest = controller;
    cartTimer = setTimeout(function () { controller.abort(); }, 12000);
    fetch(cartEndpoint(form, false), {
      credentials: "same-origin", cache: "no-store", signal: controller.signal,
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Cart request failed");
        return res.json();
      })
      .then(function (cart) {
        if (version !== navigationVersion || controller.signal.aborted) return;
        if (!sameQuantities(updates, cartUpdates(form)) || !sameQuantities(baseline, cartBaseline(form, updates))) {
          throw new Error("Cart form changed during checkout");
        }
        if (!updates || cartMatchesUpdates(cart, updates)) return cart;
        // A stale drawer must not overwrite a quantity changed by the main cart
        // or another tab. Missing rendered baselines never authorize a write.
        if (!baseline || !cartMatchesUpdates(cart, baseline)) throw new Error("Cart changed before checkout");
        return fetch(cartEndpoint(form, true), {
          method: "POST", credentials: "same-origin", cache: "no-store", signal: controller.signal,
          headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates: updates }),
        }).then(function (res) {
          if (!res.ok) throw new Error("Cart update failed");
          return res.json();
        });
      })
      .then(function (cart) {
        if (version !== navigationVersion || controller.signal.aborted) return;
        // This checks the returned snapshot, not a cross-tab cart transaction.
        // Changed/removed keys or another line appearing require a fresh review.
        if (!cartMatchesUpdates(cart, updates)) throw new Error("Cart changed during checkout");
        clearTimeout(cartTimer);
        cartRequest = null;
        if (!cart.items || cart.items.length === 0) {
          redirecting = false;
          return;
        }
        var payload = {
          items: cart.items.map(function (item) {
            return { id: item.variant_id, quantity: item.quantity };
          }),
          token: cart.token,
        };
        submitPayload(payload);
      })
      .catch(function (err) {
        if (version !== navigationVersion) return;
        clearTimeout(cartTimer);
        cartRequest = null;
        console.error("[whop-bridge] failed to read cart", err);
        redirecting = false;
        showCartError(form);
      });
  }

  function isCheckoutTrigger(el) {
    if (!el) return false;
    if (el.matches && el.matches('[name="checkout"], [href="/checkout"], [href^="/checkout?"], .shopify-payment-button__button')) return true;
    return false;
  }

  document.addEventListener(
    "click",
    function (event) {
      var el = event.target;
      while (el && el !== document.body) {
        if (isCheckoutTrigger(el)) {
          // Buy now does not add the selected product to Shopify's cart first.
          // Never substitute an unrelated cart when its form is unavailable.
          var isProductButton = el.matches(".shopify-payment-button__button");
          var payload = isProductButton ? productPayload(el) : null;
          if (isProductButton && !payload) return;
          if (el.disabled || el.getAttribute("aria-disabled") === "true") return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (isProductButton) {
            if (redirecting) return;
            redirecting = true;
            submitPayload(payload);
            return;
          }
          goToBridge(el.form || (el.closest && el.closest("form")));
          return;
        }
        el = el.parentElement;
      }
    },
    true,
  );

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (form && form.action && /\/cart/.test(form.action)) {
        // A cart form submitting straight to checkout
        var submitter = event.submitter;
        if (submitter && submitter.name === "checkout") {
          event.preventDefault();
          event.stopImmediatePropagation();
          goToBridge(form);
        }
      }
    },
    true,
  );
})();
